import { useState, useMemo } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { PieChart } from "@/components/mortgage/PieChart"
import { useDebounce } from "@/MyCode/hooks/useDebounce"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"

interface PaymentSchedule {
    month: number
    date: string
    year: number
    payment: number
    principal: number
    interest: number
    balance: number
}

export function MortgagePayoffCalculator() {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    const [loanAmount, setLoanAmount] = useState(400000)
    const [startMonth, setStartMonth] = useState(currentMonth)
    const [startYear, setStartYear] = useState(currentYear - 5) // Default to 5 years ago
    const [loanTerm, setLoanTerm] = useState(30)
    const [interestRate, setInterestRate] = useState(4.5)

    // Payment options - using radio button (exclusive)
    const [paymentOption, setPaymentOption] = useState("normalRepayment") // "normalRepayment" | "paybackRestNow" | "repaymentWithExtra"
    const [extraMonthlyPay, setExtraMonthlyPay] = useState(0)
    const [extraYearlyPay, setExtraYearlyPay] = useState(0)
    const [extraOneTimePay, setExtraOneTimePay] = useState(0)

    // Debounced values
    const debouncedLoanAmount = useDebounce(loanAmount, 500)
    const debouncedInterestRate = useDebounce(interestRate, 500)
    const debouncedLoanTerm = useDebounce(loanTerm, 500)
    const debouncedExtraMonthlyPay = useDebounce(extraMonthlyPay, 500)
    const debouncedExtraYearlyPay = useDebounce(extraYearlyPay, 500)
    const debouncedExtraOneTimePay = useDebounce(extraOneTimePay, 500)

    const yearOptions = Array.from({ length: 101 }, (_, i) => currentYear - 50 + i)

    const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ]

    // Calculate how many months have passed since loan start
    const monthsSinceStart = useMemo(() => {
        const startDate = new Date(startYear, startMonth - 1, 1)
        const currentDate = new Date(currentYear, currentMonth - 1, 1)
        const monthsDiff = (currentDate.getFullYear() - startDate.getFullYear()) * 12 +
            (currentDate.getMonth() - startDate.getMonth())
        return Math.max(0, monthsDiff)
    }, [startYear, startMonth, currentYear, currentMonth])

    // Calculate remaining balance and schedule for normal repayment
    const normalRepaymentSchedule = useMemo(() => {
        const schedule: PaymentSchedule[] = []
        const monthlyRate = debouncedInterestRate / 100 / 12
        const totalPayments = debouncedLoanTerm * 12
        const remainingPayments = totalPayments - monthsSinceStart

        if (remainingPayments <= 0) {
            return { schedule: [], remainingBalance: 0, totalInterest: 0, totalPayments: 0, monthlyPayment: 0 }
        }

        // Calculate original monthly payment
        const monthlyPayment = monthlyRate === 0
            ? debouncedLoanAmount / totalPayments
            : (debouncedLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)

        // Calculate current balance (what would be remaining after months that have passed)
        let balance = debouncedLoanAmount
        for (let month = 1; month <= monthsSinceStart && balance > 0.01; month++) {
            const interest = balance * monthlyRate
            const principal = monthlyPayment - interest
            balance -= principal
        }

        // Store the remaining balance BEFORE calculating remaining payments
        const currentRemainingBalance = balance > 0.01 ? balance : 0

        let totalInterest = 0
        let totalPaymentsMade = 0

        // Calculate remaining payments schedule (for display purposes)
        let scheduleBalance = currentRemainingBalance
        for (let month = 1; month <= remainingPayments && scheduleBalance > 0.01; month++) {
            const interest = scheduleBalance * monthlyRate
            const principal = monthlyPayment - interest
            scheduleBalance -= principal
            totalInterest += interest
            totalPaymentsMade += monthlyPayment

            const date = new Date(startYear, startMonth - 1 + monthsSinceStart + month - 1, 1)
            schedule.push({
                month: monthsSinceStart + month,
                date: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
                year: date.getFullYear(),
                payment: monthlyPayment,
                principal,
                interest,
                balance: Math.max(0, scheduleBalance),
            })
        }

        return {
            schedule,
            remainingBalance: currentRemainingBalance,
            totalInterest,
            totalPayments: totalPaymentsMade,
            monthlyPayment,
        }
    }, [debouncedLoanAmount, debouncedInterestRate, debouncedLoanTerm, monthsSinceStart, startYear, startMonth, monthNames])

    // Calculate schedule with extra payments
    const extraPaymentSchedule = useMemo(() => {
        if (paymentOption !== "repaymentWithExtra") {
            return { schedule: [], remainingBalance: 0, totalInterest: 0, totalPayments: 0, monthsToPayoff: 0 }
        }

        const schedule: PaymentSchedule[] = []
        const monthlyRate = debouncedInterestRate / 100 / 12
        let balance = normalRepaymentSchedule.remainingBalance

        // Apply one-time payment if specified
        if (debouncedExtraOneTimePay > 0) {
            balance = Math.max(0, balance - debouncedExtraOneTimePay)
        }

        const monthlyPayment = normalRepaymentSchedule.monthlyPayment || 0
        let totalInterest = 0
        let totalPaymentsMade = 0
        let month = 1

        while (balance > 0.01 && month <= 600) { // Max 50 years
            const interest = balance * monthlyRate
            let principal = monthlyPayment - interest
            let extraPrincipal = 0

            // Add extra monthly payment
            if (debouncedExtraMonthlyPay > 0) {
                extraPrincipal += debouncedExtraMonthlyPay
            }

            // Add extra yearly payment (every 12 months)
            if (debouncedExtraYearlyPay > 0 && month % 12 === 0) {
                extraPrincipal += debouncedExtraYearlyPay
            }

            principal += extraPrincipal
            balance -= principal
            totalInterest += interest
            totalPaymentsMade += monthlyPayment + extraPrincipal

            const date = new Date(startYear, startMonth - 1 + monthsSinceStart + month - 1, 1)
            schedule.push({
                month: monthsSinceStart + month,
                date: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
                year: date.getFullYear(),
                payment: monthlyPayment + extraPrincipal,
                principal,
                interest,
                balance: Math.max(0, balance),
            })

            month++
        }

        return {
            schedule,
            remainingBalance: balance > 0.01 ? balance : 0,
            totalInterest,
            totalPayments: totalPaymentsMade,
            monthsToPayoff: month - 1,
        }
    }, [
        paymentOption,
        normalRepaymentSchedule.remainingBalance,
        normalRepaymentSchedule.monthlyPayment,
        debouncedInterestRate,
        debouncedExtraMonthlyPay,
        debouncedExtraYearlyPay,
        debouncedExtraOneTimePay,
        startYear,
        startMonth,
        monthsSinceStart,
    ])

    // Calculate payback rest now scenario
    const paybackNowScenario = useMemo(() => {
        if (paymentOption !== "paybackRestNow") {
            return { remainingBalance: 0, interestSaved: 0, monthsSaved: 0 }
        }

        const remainingBalance = normalRepaymentSchedule.remainingBalance
        const totalInterestRemaining = normalRepaymentSchedule.totalInterest
        const monthsRemaining = normalRepaymentSchedule.schedule.length

        return {
            remainingBalance,
            interestSaved: totalInterestRemaining,
            monthsSaved: monthsRemaining,
        }
    }, [paymentOption, normalRepaymentSchedule])

    // Calculate comparison
    const comparison = useMemo(() => {
        if (paymentOption !== "repaymentWithExtra") {
            return null
        }

        const normalMonths = normalRepaymentSchedule.schedule.length
        const extraMonths = extraPaymentSchedule.monthsToPayoff
        const monthsSaved = normalMonths - extraMonths
        const interestSaved = normalRepaymentSchedule.totalInterest - extraPaymentSchedule.totalInterest

        return {
            monthsSaved,
            yearsSaved: Math.floor(monthsSaved / 12),
            remainingMonthsSaved: monthsSaved % 12,
            interestSaved,
            remainingBalance: normalRepaymentSchedule.remainingBalance,
        }
    }, [paymentOption, normalRepaymentSchedule, extraPaymentSchedule])

    // Calculate pie chart data
    const pieChartData = useMemo(() => {
        const remainingBalance = normalRepaymentSchedule.remainingBalance
        const principalPaid = debouncedLoanAmount - remainingBalance
        const monthlyRate = debouncedInterestRate / 100 / 12
        const totalPayments = debouncedLoanTerm * 12
        const monthlyPayment = monthlyRate === 0
            ? debouncedLoanAmount / totalPayments
            : (debouncedLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
            (Math.pow(1 + monthlyRate, totalPayments) - 1)

        // Calculate interest paid so far
        let interestPaid = 0
        let balance = debouncedLoanAmount
        for (let month = 1; month <= monthsSinceStart && balance > 0.01; month++) {
            const interest = balance * monthlyRate
            interestPaid += interest
            const principal = monthlyPayment - interest
            balance -= principal
        }

        return [
            {
                label: "Principal Paid",
                value: Math.max(0, principalPaid),
                color: "#3b82f6", // blue
            },
            {
                label: "Interest Paid",
                value: Math.max(0, interestPaid),
                color: "#ef4444", // red
            },
            {
                label: "Remaining Balance",
                value: Math.max(0, remainingBalance),
                color: "#f59e0b", // amber
            },
        ]
    }, [debouncedLoanAmount, debouncedInterestRate, debouncedLoanTerm, monthsSinceStart, normalRepaymentSchedule.remainingBalance])

    // Generate month options
    const monthOptions = monthNames.map((month, index) => ({
        label: month,
        value: String(index + 1)
    }))

    // Generate year options
    const yearSelectOptions = yearOptions.map(year => ({
        label: String(year),
        value: String(year)
    }))

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.loanAmount !== undefined) setLoanAmount(Number(data.loanAmount))
        if (data.startMonth !== undefined) setStartMonth(Number(data.startMonth))
        if (data.startYear !== undefined) setStartYear(Number(data.startYear))
        if (data.loanTerm !== undefined) setLoanTerm(Number(data.loanTerm))
        if (data.interestRate !== undefined) setInterestRate(Number(data.interestRate))
        if (data.paymentOption !== undefined) setPaymentOption(data.paymentOption)
        if (data.extraMonthlyPay !== undefined) setExtraMonthlyPay(Number(data.extraMonthlyPay))
        if (data.extraYearlyPay !== undefined) setExtraYearlyPay(Number(data.extraYearlyPay))
        if (data.extraOneTimePay !== undefined) setExtraOneTimePay(Number(data.extraOneTimePay))
    }

    const steps: Step[] = [
        {
            id: "loan-details",
            title: "Loan Details",
            description: "Enter your mortgage loan information",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanAmount",
                    label: "Loan Amount",
                    placeholder: "400000",
                    required: true,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [1000, 10000, 100000],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanAmount: 'required|numeric|min:0'
                    }
                },
                {
                    type: "select",
                    name: "startMonth",
                    label: "When did you take (Month)",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: monthOptions
                    },
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        startMonth: 'required|string|in:1,2,3,4,5,6,7,8,9,10,11,12'
                    }
                },
                {
                    type: "select",
                    name: "startYear",
                    label: "When did you take (Year)",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: yearSelectOptions
                    },
                    ui: { colSpan: 6 },
                    laravelValidationRules: {
                        startYear: 'required|string'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanTerm",
                    label: "Loan Term (years)",
                    placeholder: "30",
                    required: true,
                    minValue: 1,
                    maxValue: 50,
                    allowNegative: false,
                    incrementValues: [1, 5],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        loanTerm: 'required|integer|min:1|max:50'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "interestRate",
                    label: "Interest Rate (%)",
                    placeholder: "4.5",
                    required: true,
                    minValue: 0,
                    maxValue: 100,
                    allowNegative: false,
                    incrementValues: [1, 0.1, 0.01],
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        interestRate: 'required|numeric|min:0|max:100'
                    }
                },
            ]
        },
        {
            id: "payment-options",
            title: "Payment Options",
            description: "Choose your payment strategy",
            fields: [
                {
                    type: "radio",
                    name: "paymentOption",
                    label: "Payment Strategy",
                    required: true,
                    options: {
                        source: "static" as const,
                        items: [
                            { value: "normalRepayment", label: "Normal repayment" },
                            { value: "paybackRestNow", label: "Payback rest altogether now" },
                            { value: "repaymentWithExtra", label: "Repayment with extra payment" },
                        ]
                    },
                    ui: { colSpan: 12 },
                    laravelValidationRules: {
                        paymentOption: 'required|string|in:normalRepayment,paybackRestNow,repaymentWithExtra'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "extraMonthlyPay",
                    label: "Extra Payment Per Month",
                    placeholder: "0",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 12 },
                    visibleWhen: [
                        { field: "paymentOption", op: "=" as const, value: "repaymentWithExtra" }
                    ],
                    laravelValidationRules: {
                        extraMonthlyPay: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "extraYearlyPay",
                    label: "Extra Payment Per Year",
                    placeholder: "0",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 12 },
                    visibleWhen: [
                        { field: "paymentOption", op: "=" as const, value: "repaymentWithExtra" }
                    ],
                    laravelValidationRules: {
                        extraYearlyPay: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "extraOneTimePay",
                    label: "Extra Payment One Time",
                    placeholder: "0",
                    required: false,
                    minValue: 0,
                    maxValue: 1000000000,
                    allowNegative: false,
                    incrementValues: [10, 100, 1000],
                    ui: { colSpan: 12 },
                    visibleWhen: [
                        { field: "paymentOption", op: "=" as const, value: "repaymentWithExtra" }
                    ],
                    laravelValidationRules: {
                        extraOneTimePay: 'nullable|numeric|min:0'
                    }
                },
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">Mortgage Payoff Calculator</h1>
                <p className="text-muted-foreground">
                    Calculate how extra payments can help you pay off your mortgage faster and save on interest.
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            loanAmount: loanAmount,
                            startMonth: String(startMonth),
                            startYear: String(startYear),
                            loanTerm: loanTerm,
                            interestRate: interestRate,
                            paymentOption: paymentOption,
                            extraMonthlyPay: extraMonthlyPay,
                            extraYearlyPay: extraYearlyPay,
                            extraOneTimePay: extraOneTimePay,
                        }}
                    />
                </motion.div>

                {/* Right Side - Results */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-4"
                >
                    {/* Pie Chart */}
                    <div className="bg-card border border-border rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Payment Breakdown</h3>
                        <div className="flex justify-center">
                            <PieChart data={pieChartData} size={280} />
                        </div>
                    </div>

                    {/* Current Status */}
                    <div className="bg-card border border-border rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Current Status</h3>
                        <div className="space-y-3">
                            <div>
                                <div className="text-sm text-muted-foreground mb-1">Months Since Start</div>
                                <div className="text-xl font-bold">{monthsSinceStart} months</div>
                            </div>
                            <div>
                                <div className="text-sm text-muted-foreground mb-1">Remaining Balance</div>
                                <div className="text-xl font-bold">
                                    $<AnimatedNumber value={normalRepaymentSchedule.remainingBalance} decimals={2} />
                                </div>
                            </div>
                            <div>
                                <div className="text-sm text-muted-foreground mb-1">Remaining Payments</div>
                                <div className="text-xl font-bold">{normalRepaymentSchedule.schedule.length} months</div>
                            </div>
                        </div>
                    </div>

                    {/* Payback Now Scenario */}
                    {paymentOption === "paybackRestNow" && (
                        <div className="bg-card border-2 border-primary/20 rounded-lg p-4">
                            <h3 className="text-lg font-semibold mb-4 text-primary">Payback Rest Now</h3>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-muted-foreground mb-1">Amount to Pay</div>
                                    <div className="text-xl font-bold text-primary">
                                        $<AnimatedNumber value={paybackNowScenario.remainingBalance} decimals={2} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-muted-foreground mb-1">Interest Saved</div>
                                    <div className="text-xl font-bold text-primary">
                                        $<AnimatedNumber value={paybackNowScenario.interestSaved} decimals={2} />
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-muted-foreground mb-1">Months Saved</div>
                                    <div className="text-xl font-bold text-primary">
                                        {paybackNowScenario.monthsSaved} months
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Extra Payment Comparison */}
                    {paymentOption === "repaymentWithExtra" && comparison && (
                        <div className="bg-card border-2 border-primary/20 rounded-lg p-4">
                            <h3 className="text-lg font-semibold mb-4 text-primary">With Extra Payments</h3>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-muted-foreground mb-1">Payoff Time</div>
                                    <div className="text-xl font-bold text-primary">
                                        {Math.floor(extraPaymentSchedule.monthsToPayoff / 12)} years {extraPaymentSchedule.monthsToPayoff % 12} months
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-muted-foreground mb-1">Time Saved</div>
                                    <div className="text-xl font-bold text-primary">
                                        {comparison.yearsSaved > 0 && `${comparison.yearsSaved} year${comparison.yearsSaved > 1 ? 's' : ''} `}
                                        {comparison.remainingMonthsSaved > 0 && `${comparison.remainingMonthsSaved} month${comparison.remainingMonthsSaved > 1 ? 's' : ''}`}
                                        {comparison.yearsSaved === 0 && comparison.remainingMonthsSaved === 0 && '0 months'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-muted-foreground mb-1">Interest Saved</div>
                                    <div className="text-xl font-bold text-primary">
                                        $<AnimatedNumber value={comparison.interestSaved} decimals={2} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>

            {/* Comparison Explanation */}
            {paymentOption === "repaymentWithExtra" && comparison && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                    className="bg-card border border-border rounded-lg p-6"
                >
                    <h2 className="text-2xl font-bold mb-4">Comparison</h2>
                    <div className="space-y-4">
                        <div className="prose max-w-none">
                            <p className="text-base leading-relaxed">
                                The remaining balance is <span className="font-bold">$<AnimatedNumber value={comparison.remainingBalance} decimals={2} /></span>.
                                {debouncedExtraMonthlyPay > 0 && (
                                    <> By paying extra <span className="font-bold">$<AnimatedNumber value={debouncedExtraMonthlyPay} decimals={2} /></span> per month starting now,</>
                                )}
                                {debouncedExtraYearlyPay > 0 && !debouncedExtraMonthlyPay && (
                                    <> By paying extra <span className="font-bold">$<AnimatedNumber value={debouncedExtraYearlyPay} decimals={2} /></span> per year starting now,</>
                                )}
                                {debouncedExtraOneTimePay > 0 && !debouncedExtraMonthlyPay && !debouncedExtraYearlyPay && (
                                    <> By making a one-time payment of <span className="font-bold">$<AnimatedNumber value={debouncedExtraOneTimePay} decimals={2} /></span> now,</>
                                )}
                                {debouncedExtraMonthlyPay > 0 && debouncedExtraYearlyPay > 0 && (
                                    <> By paying extra <span className="font-bold">$<AnimatedNumber value={debouncedExtraMonthlyPay} decimals={2} /></span> per month and <span className="font-bold">$<AnimatedNumber value={debouncedExtraYearlyPay} decimals={2} /></span> per year starting now,</>
                                )}
                                {debouncedExtraOneTimePay > 0 && (debouncedExtraMonthlyPay > 0 || debouncedExtraYearlyPay > 0) && (
                                    <> along with a one-time payment of <span className="font-bold">$<AnimatedNumber value={debouncedExtraOneTimePay} decimals={2} /></span>,</>
                                )}
                                {" "}the loan will be paid off in{" "}
                                <span className="font-bold">
                                    {Math.floor(extraPaymentSchedule.monthsToPayoff / 12)} years and {extraPaymentSchedule.monthsToPayoff % 12} months
                                </span>.
                                {comparison.yearsSaved > 0 || comparison.remainingMonthsSaved > 0 ? (
                                    <>
                                        {" "}It is{" "}
                                        <span className="font-bold text-primary">
                                            {comparison.yearsSaved > 0 && `${comparison.yearsSaved} year${comparison.yearsSaved > 1 ? 's' : ''} `}
                                            {comparison.remainingMonthsSaved > 0 && `${comparison.remainingMonthsSaved} month${comparison.remainingMonthsSaved > 1 ? 's' : ''}`}
                                        </span>{" "}
                                        earlier.
                                    </>
                                ) : null}
                                {" "}This results in savings of{" "}
                                <span className="font-bold text-primary">
                                    $<AnimatedNumber value={comparison.interestSaved} decimals={2} />
                                </span>{" "}
                                in interest.
                            </p>
                        </div>

                        {/* Comparison Table */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <div className="bg-muted/50 rounded-lg p-4">
                                <h3 className="font-semibold mb-3">Normal Repayment</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Remaining Payments:</span>
                                        <span className="font-semibold">{normalRepaymentSchedule.schedule.length} months</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Remaining Interest:</span>
                                        <span className="font-semibold">
                                            $<AnimatedNumber value={normalRepaymentSchedule.totalInterest} decimals={2} />
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Total Remaining:</span>
                                        <span className="font-semibold">
                                            $<AnimatedNumber value={normalRepaymentSchedule.totalPayments} decimals={2} />
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-primary/10 rounded-lg p-4">
                                <h3 className="font-semibold mb-3 text-primary">With Extra Payments</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Remaining Payments:</span>
                                        <span className="font-semibold text-primary">{extraPaymentSchedule.monthsToPayoff} months</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Remaining Interest:</span>
                                        <span className="font-semibold text-primary">
                                            $<AnimatedNumber value={extraPaymentSchedule.totalInterest} decimals={2} />
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Total Remaining:</span>
                                        <span className="font-semibold text-primary">
                                            $<AnimatedNumber value={extraPaymentSchedule.totalPayments} decimals={2} />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Payback Now Explanation */}
            {paymentOption === "paybackRestNow" && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    className="bg-card border-2 border-primary/20 rounded-lg p-6"
                >
                    <h2 className="text-2xl font-bold mb-4 text-primary">Payback Rest Now</h2>
                    <div className="prose max-w-none">
                        <p className="text-base leading-relaxed">
                            The remaining balance is <span className="font-bold">$<AnimatedNumber value={paybackNowScenario.remainingBalance} decimals={2} /></span>.
                            By paying this amount now, you will save{" "}
                            <span className="font-bold text-primary">
                                $<AnimatedNumber value={paybackNowScenario.interestSaved} decimals={2} />
                            </span>{" "}
                            in interest and eliminate{" "}
                            <span className="font-bold text-primary">
                                {paybackNowScenario.monthsSaved} months
                            </span>{" "}
                            of remaining payments.
                        </p>
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
}

